import { useTranslation } from "react-i18next";

/**
 * Shown after a premium meal swap on a persisted menu: the swapped plan is a
 * local draft until the user explicitly saves it (BRD §5, audit C1).
 */
export function SwapPendingBanner({
  onSave,
  isSaving,
}: {
  onSave: () => void;
  isSaving: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div
      role="status"
      className="flex items-center justify-between gap-3 border-b border-accent/30 bg-accent/5 px-5 py-3"
    >
      <div className="min-w-0">
        <p className="text-[12px] font-semibold text-foreground">
          {t("menuSwap.pendingTitle")}
        </p>
        <p className="text-[11px] text-ink-50">{t("menuSwap.pendingBody")}</p>
      </div>
      <button
        type="button"
        onClick={onSave}
        disabled={isSaving}
        className="shrink-0 rounded-full bg-accent px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-accent-foreground disabled:opacity-60"
      >
        {isSaving ? t("menuSwap.saving") : t("menuSwap.save")}
      </button>
    </div>
  );
}

/**
 * Shown when /menu holds a draft that is not persisted yet (e.g. the user
 * left the reveal without validating). The draft is saved only when the user
 * taps the button — never automatically (BRD §5, audit M14).
 */
export function DraftPendingBanner({
  onValidate,
  isValidating,
}: {
  onValidate: () => void;
  isValidating: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div
      role="status"
      className="flex items-center justify-between gap-3 border-b border-accent/30 bg-accent/5 px-5 py-3"
    >
      <div className="min-w-0">
        <p className="text-[12px] font-semibold text-foreground">
          {t("menuDraft.pendingTitle")}
        </p>
        <p className="text-[11px] text-ink-50">{t("menuDraft.pendingBody")}</p>
      </div>
      <button
        type="button"
        onClick={onValidate}
        disabled={isValidating}
        className="shrink-0 rounded-full bg-accent px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-accent-foreground disabled:opacity-60"
      >
        {isValidating ? t("menuDraft.validating") : t("menuDraft.validate")}
      </button>
    </div>
  );
}
